import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { COMMUNE_NOM_DEFAUT, communeValide, evenementSchema } from '@dinertournant/core';
import { BandeauAlerte, Champ } from '../../components/index.js';
import { ErreurApi, api, heureInput, slugDe, type Evenement } from '../../lib/api.js';

export interface ValeursEvenement {
  nom: string;
  organisation?: string;
  description?: string;
  date: string;
  zone?: string;
  horaireEntree: string;
  horairePlat: string;
  horaireDessert: string;
  dateLimiteInscription: string;
  capaciteMax?: number | undefined;
  tailleMin: number;
  tailleMax: number;
  slug: string;
  revealAdresseMinAvant?: number | null;
  contactNom?: string;
  contactTelephone?: string;
  communeActive: boolean;
  communeNom?: string;
  communeHeure?: string;
  communeLieu?: string;
  communeAdresse?: string;
  communeComplement?: string;
}

const defauts: ValeursEvenement = {
  nom: '',
  organisation: '',
  description: '',
  date: '',
  zone: '',
  horaireEntree: '19:30',
  horairePlat: '20:45',
  horaireDessert: '22:15',
  dateLimiteInscription: '',
  capaciteMax: undefined,
  tailleMin: 3,
  tailleMax: 5,
  slug: '',
  revealAdresseMinAvant: null,
  contactNom: '',
  contactTelephone: '',
  communeActive: false,
  communeNom: '',
  communeHeure: '23:30',
  communeLieu: '',
  communeAdresse: '',
  communeComplement: '',
};

function depuisEvenement(e: Evenement): ValeursEvenement {
  return {
    nom: e.nom,
    organisation: e.organisation ?? '',
    description: e.description ?? '',
    date: e.date,
    zone: e.zone ?? '',
    horaireEntree: heureInput(e.horaireEntree),
    horairePlat: heureInput(e.horairePlat),
    horaireDessert: heureInput(e.horaireDessert),
    dateLimiteInscription: e.dateLimiteInscription,
    capaciteMax: e.capaciteMax ?? undefined,
    tailleMin: e.tailleMin ?? 3,
    tailleMax: e.tailleMax ?? 5,
    slug: e.slug,
    revealAdresseMinAvant: e.revealAdresseMinAvant,
    contactNom: e.contactNom ?? '',
    contactTelephone: e.contactTelephone ?? '',
    communeActive: e.communeActive ?? false,
    communeNom: e.communeNom ?? '',
    communeHeure: e.communeHeure ? heureInput(e.communeHeure) : '23:30',
    communeLieu: e.communeLieu ?? '',
    communeAdresse: e.communeAdresse ?? '',
    communeComplement: e.communeComplement ?? '',
  };
}

/** Nettoie les valeurs du formulaire avant validation Zod (chaînes vides → absent, nombres). */
function normaliser(v: ValeursEvenement) {
  const vide = (s: string | undefined) => (s && s.trim() ? s.trim() : undefined);
  const cap = v.capaciteMax === undefined || v.capaciteMax === null || Number.isNaN(Number(v.capaciteMax)) || String(v.capaciteMax) === '' ? undefined : Number(v.capaciteMax);
  const reveal = v.revealAdresseMinAvant === null || v.revealAdresseMinAvant === undefined || String(v.revealAdresseMinAvant) === '' ? null : Number(v.revealAdresseMinAvant);
  return {
    nom: v.nom.trim(),
    organisation: vide(v.organisation),
    description: vide(v.description),
    date: v.date,
    zone: vide(v.zone),
    horaireEntree: v.horaireEntree,
    horairePlat: v.horairePlat,
    horaireDessert: v.horaireDessert,
    dateLimiteInscription: v.dateLimiteInscription,
    capaciteMax: cap,
    tailleMin: Number(v.tailleMin) || 3,
    tailleMax: Number(v.tailleMax) || 5,
    slug: v.slug.trim(),
    revealAdresseMinAvant: reveal,
    contactNom: vide(v.contactNom),
    contactTelephone: vide(v.contactTelephone),
    communeActive: !!v.communeActive,
    communeNom: vide(v.communeNom),
    communeHeure: vide(v.communeHeure),
    communeLieu: vide(v.communeLieu),
    communeAdresse: vide(v.communeAdresse),
    communeComplement: vide(v.communeComplement),
  };
}

interface Props {
  /** Événement à modifier ; absent = création (écran 1). */
  evenement?: Evenement | undefined;
  onEnregistre?: ((e: Evenement) => void) | undefined;
}

export function EvenementForm({ evenement, onEnregistre }: Props) {
  const navigate = useNavigate();
  const [erreurGlobale, setErreurGlobale] = useState<string | null>(null);
  const [slugManuel, setSlugManuel] = useState(!!evenement);
  const { register, handleSubmit, watch, setValue, setError, formState } = useForm<ValeursEvenement>({
    resolver: (async (valeurs: ValeursEvenement, ctx: unknown, opts: unknown) => {
      const n = normaliser(valeurs);
      const r = (await (zodResolver(evenementSchema) as unknown as (v: unknown, c: unknown, o: unknown) => Promise<{ values: unknown; errors: Record<string, unknown> }>)(n, ctx, opts)) as { values: unknown; errors: Record<string, unknown> };
      // Étape commune : heure, lieu et adresse obligatoires quand elle est activée.
      const commune = communeValide(n);
      if (!commune.length) return r;
      const errors = { ...r.errors };
      for (const e of commune) errors[e.path] = { type: 'custom', message: e.message };
      return { values: {}, errors };
    }) as Resolver<ValeursEvenement>,
    defaultValues: evenement ? depuisEvenement(evenement) : defauts,
    mode: 'onTouched',
  });
  const { errors, isSubmitting } = formState;
  const nom = watch('nom');
  const communeActive = watch('communeActive');

  useEffect(() => {
    if (!slugManuel) setValue('slug', slugDe(nom ?? ''));
  }, [nom, slugManuel, setValue]);

  const onSubmit = handleSubmit(async (valeurs) => {
    setErreurGlobale(null);
    const corps = normaliser(valeurs);
    try {
      const r = evenement
        ? await api.put<{ event: Evenement }>(`/api/events/${evenement.id}`, corps)
        : await api.post<{ event: Evenement }>('/api/events', corps);
      if (onEnregistre) onEnregistre(r.event);
      else navigate(`/app/e/${r.event.id}/evenement`);
    } catch (e) {
      if (e instanceof ErreurApi) {
        for (const i of e.issues) setError(i.path as keyof ValeursEvenement, { message: i.message });
        setErreurGlobale(e.issues.length ? 'Vérifiez les champs signalés.' : e.message);
      } else setErreurGlobale('Impossible de joindre le serveur.');
    }
  });

  return (
    <form className="pile formulaire-evenement" onSubmit={onSubmit} noValidate>
      {erreurGlobale ? <BandeauAlerte niveau="attention" role="alert" titre={erreurGlobale} /> : null}

      <section className="carte pile" aria-labelledby="ev-s1">
        <div className="titre-section">
          <span className="sec-num" aria-hidden="true">
            1
          </span>
          <h2 id="ev-s1">L'événement</h2>
        </div>
        <Champ id="nom" label="Nom de l'événement" placeholder="Dîner tournant des Chartrons" erreur={errors.nom?.message} {...register('nom')} />
        <Champ id="organisation" label="Organisation" optionnel="affichée en surtitre" placeholder="Comité de quartier des Chartrons" erreur={errors.organisation?.message} {...register('organisation')} />
        <div className="pile-6">
          <label className="f-label" htmlFor="description">
            Présentation <span className="optionnel">(en tête du formulaire d'inscription)</span>
          </label>
          <textarea id="description" className="f-input" rows={3} placeholder="Une entrée, un plat, un dessert : trois tables chez trois Lions différents." {...register('description')} />
        </div>
        <Champ id="zone" label="Quartier ou zone" optionnel="facultatif" placeholder="Les Chartrons, Bordeaux" erreur={errors.zone?.message} {...register('zone')} />
      </section>

      <section className="carte pile" aria-labelledby="ev-s2">
        <div className="titre-section">
          <span className="sec-num" aria-hidden="true">
            2
          </span>
          <h2 id="ev-s2">La soirée</h2>
        </div>
        <div className="grille-2">
          <Champ id="date" label="Date" type="date" erreur={errors.date?.message} {...register('date')} />
          <Champ id="dateLimiteInscription" label="Fin des inscriptions" type="date" erreur={errors.dateLimiteInscription?.message} {...register('dateLimiteInscription')} />
        </div>
        <div className="grille-3">
          <Champ id="horaireEntree" label="Entrée" type="time" erreur={errors.horaireEntree?.message} {...register('horaireEntree')} />
          <Champ id="horairePlat" label="Plat" type="time" erreur={errors.horairePlat?.message} {...register('horairePlat')} />
          <Champ id="horaireDessert" label="Dessert" type="time" erreur={errors.horaireDessert?.message} {...register('horaireDessert')} />
        </div>
        <div className="grille-2">
          <div className="pile-6">
            <span className="f-label" id="tailles-label">
              Convives par table, hôte compris
            </span>
            <div className="ligne" style={{ gap: 10 }} role="group" aria-labelledby="tailles-label">
              <label className="sr-only" htmlFor="tailleMin">
                Minimum
              </label>
              <span className="texte-muet">de</span>
              <select id="tailleMin" className="f-input" style={{ width: 'auto' }} {...register('tailleMin', { setValueAs: (v) => Number(v) })}>
                {Array.from({ length: 19 }, (_, i) => i + 2).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="texte-muet">à</span>
              <label className="sr-only" htmlFor="tailleMax">
                Maximum
              </label>
              <select id="tailleMax" className="f-input" style={{ width: 'auto' }} {...register('tailleMax', { setValueAs: (v) => Number(v) })}>
                {Array.from({ length: 19 }, (_, i) => i + 2).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="texte-muet">personnes</span>
            </div>
            {Number(watch('tailleMin')) > Number(watch('tailleMax')) ? (
              <span className="f-erreur" role="alert">
                Le minimum doit être inférieur ou égal au maximum.
              </span>
            ) : null}
            <span className="f-hint">
              Nombre d'hôtes à trouver par étape : participants ÷ {Math.round((Number(watch('tailleMin') || 3) + Number(watch('tailleMax') || 5)) / 2)}, plus si les capacités déclarées des hôtes ne suffisent pas. Modifiable jusqu'à la répartition.
            </span>
          </div>
          <Champ id="capaciteMax" label="Places maximum" optionnel="vide = illimité" type="number" min={1} inputMode="numeric" erreur={errors.capaciteMax?.message} {...register('capaciteMax', { setValueAs: (v) => (v === '' ? undefined : Number(v)) })} />
          <div className="pile-6">
            <label className="f-label" htmlFor="revealAdresseMinAvant">
              Adresses surprises
            </label>
            <select id="revealAdresseMinAvant" className="f-input" {...register('revealAdresseMinAvant', { setValueAs: (v) => (v === '' || v === null ? null : Number(v)) })}>
              <option value="">Non : adresses visibles dès l'envoi du parcours</option>
              <option value="15">Révélées 15 min avant chaque étape</option>
              <option value="30">Révélées 30 min avant chaque étape</option>
              <option value="60">Révélées 1 h avant chaque étape</option>
            </select>
            <span className="f-hint">Chaque convive ne voit que l'adresse de sa table, jamais les autres.</span>
          </div>
        </div>
      </section>

      <section className="carte pile" aria-labelledby="ev-s5">
        <div className="titre-section">
          <span className="sec-num" aria-hidden="true">
            3
          </span>
          <h2 id="ev-s5">Une dernière étape tous ensemble</h2>
        </div>
        <label className="ligne case" htmlFor="communeActive" style={{ gap: 10, alignItems: 'flex-start' }}>
          <input id="communeActive" type="checkbox" {...register('communeActive')} />
          <span>
            <strong>Ajouter une étape commune en fin de soirée</strong>
            <span className="f-hint" style={{ display: 'block' }}>
              Tout le monde se retrouve au même endroit après le dessert. Son adresse est dévoilée comme celles des hôtes (surprise ou non), sur le parcours de chaque convive.
            </span>
          </span>
        </label>
        {communeActive ? (
          <div className="pile" style={{ gap: 14 }}>
            <div className="grille-2">
              <Champ id="communeNom" label="Nom de l'étape" optionnel={`vide = « ${COMMUNE_NOM_DEFAUT} »`} placeholder={COMMUNE_NOM_DEFAUT} erreur={errors.communeNom?.message} {...register('communeNom')} />
              <Champ id="communeHeure" label="Heure" type="time" aide="Après le dessert." erreur={errors.communeHeure?.message} {...register('communeHeure')} />
            </div>
            <Champ id="communeLieu" label="Lieu" placeholder="Chez Vincent, Salle des fêtes…" erreur={errors.communeLieu?.message} {...register('communeLieu')} />
            <div className="grille-2">
              <Champ id="communeAdresse" label="Adresse" placeholder="12 rue des Lions" erreur={errors.communeAdresse?.message} {...register('communeAdresse')} />
              <Champ id="communeComplement" label="Complément" optionnel="facultatif" placeholder="Portail vert, 2e étage…" erreur={errors.communeComplement?.message} {...register('communeComplement')} />
            </div>
          </div>
        ) : null}
      </section>

      <section className="carte pile" aria-labelledby="ev-s4">
        <div className="titre-section">
          <span className="sec-num" aria-hidden="true">
            4
          </span>
          <h2 id="ev-s4">Le jour J</h2>
        </div>
        <p className="texte-doux" style={{ fontSize: 15 }}>
          Affiché sur le parcours de chaque convive : « Un imprévu ? Prévenez… ».
        </p>
        <div className="grille-2">
          <Champ id="contactNom" label="Prénom de la personne à joindre" optionnel="facultatif" placeholder="Nathalie" erreur={errors.contactNom?.message} {...register('contactNom')} />
          <Champ id="contactTelephone" label="Téléphone le soir même" optionnel="facultatif" type="tel" placeholder="06 00 00 00 00" erreur={errors.contactTelephone?.message} {...register('contactTelephone')} />
        </div>
      </section>

      <section className="carte pile" aria-labelledby="ev-s3">
        <div className="titre-section">
          <span className="sec-num" aria-hidden="true">
            5
          </span>
          <h2 id="ev-s3">Le lien d'inscription</h2>
        </div>
        <Champ
          id="slug"
          label="Adresse du formulaire"
          aide={`Le formulaire public sera à ${window.location.origin}/i/${watch('slug') || '…'}`}
          erreur={errors.slug?.message}
          {...register('slug', { onChange: () => setSlugManuel(true) })}
        />
      </section>

      <div className="ligne" style={{ justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" className="btn" onClick={() => navigate(-1)}>
          Annuler
        </button>
        <button type="submit" className="btn principal" disabled={isSubmitting}>
          {evenement ? 'Enregistrer' : "Créer l'événement"}
        </button>
      </div>
    </form>
  );
}
